import { t } from "@lingui/macro";
import { ExpenseBudgetItemTypeEnum } from "@src/__generated__/urql-graphql";
import type { IOption } from "@src/components/ui-kit";
import { required } from "@src/utils/forms/validators";
import { NumberModel, PercentModel } from "@src/utils/models";
import { type ComposibleValidatable, FieldState, FormState } from "formstate";
import type { TExpenseApproval, TVatRates } from "../models";

export type TBudgetItemForm = ComposibleValidatable<{
  id: FieldState<string | undefined>;
  brand_id: FieldState<string>;
  project_id: FieldState<string>;
  budget_item_id: FieldState<string>;
  budget_item_type: FieldState<ExpenseBudgetItemTypeEnum>;
  accounting_account_id: FieldState<string>;
  amount_in_currency: FieldState<NumberModel>;
  amount_in_percent: FieldState<NumberModel>;
  responsible_id: FieldState<string | undefined>;
  approved: FieldState<boolean>;
  approvals: FieldState<TExpenseApproval[]>;
  track_hours: FieldState<boolean>;
  tracked_time: FieldState<number>;
  report_date: FieldState<Date | undefined>;
  time_tracking_work_type_id: FieldState<string | undefined>;
  task_id: FieldState<string | undefined>;
  vatRate: FieldState<TVatRates[0] | undefined>;
  vat_amount_in_currency: FieldState<NumberModel>;
  accounting_centre_id: FieldState<string | undefined>;
  tag_ids: FieldState<string[] | undefined>;
  description_id: FieldState<string | undefined>;
  custom_cost_ratio: FieldState<string | undefined>;
  accounting_vat_classification_id: FieldState<string | undefined>;
}>;

export const budgetItemForm = (args: {
  id?: string;
  brand_id?: string;
  project_id?: string;
  accounting_account_id?: string;
  budget_item_id?: string;
  budget_item_type?: ExpenseBudgetItemTypeEnum;
  amount_in_currency: NumberModel;
  amount_in_percent?: NumberModel;
  responsible_id?: string | null;
  approved: boolean;
  approvals: TExpenseApproval[];
  track_hours?: boolean;
  tracked_time?: number;
  report_date?: Date;
  time_tracking_work_type_id?: string;
  positions?: IOption[];
  task_id?: string;
  vatRate?: TVatRates[0] | undefined;
  vat_amount_in_currency?: NumberModel;
  amount_includes_vat?: boolean;
  accounting_centre_id?: string;
  tag_ids?: string[];
  description_id?: string;
  custom_cost_ratio?: string;
  accounting_vat_classification_id?: string;
}): TBudgetItemForm => {
  const percentModel = new PercentModel(args.vatRate?.amount ?? 0);
  const amount = args.amount_in_currency.denominatedValue;
  const vatAmountInCurrency =
    args.vat_amount_in_currency ??
    NumberModel.fromDenomValue(
      args.amount_includes_vat
        ? percentModel.vatFromGross(amount)
        : percentModel.percentOf(amount),
      args.amount_in_currency.denominator,
    );

  return new FormState({
    id: new FieldState(args.id),
    brand_id: new FieldState(args?.brand_id ?? ""),
    project_id: new FieldState(args?.project_id ?? "").validators(required),
    accounting_account_id: new FieldState(
      args?.accounting_account_id ?? "",
    ).validators(required),
    budget_item_id: new FieldState(args?.budget_item_id ?? "").validators(
      ($) => (!$ || $.startsWith("-") ? t`Required field` : null),
    ),
    budget_item_type: new FieldState(
      args?.budget_item_type ?? ExpenseBudgetItemTypeEnum.ExpenseBudgetItem,
    ),
    amount_in_currency: new FieldState(args.amount_in_currency),
    amount_in_percent: new FieldState(
      args.amount_in_percent ?? new NumberModel(0, 2),
    ),
    responsible_id: new FieldState(args?.responsible_id ?? undefined),
    approved: new FieldState(args.approved),
    approvals: new FieldState(args.approvals),
    track_hours: new FieldState(args?.track_hours ?? false),
    tracked_time: new FieldState(args?.tracked_time ?? 0),
    report_date: new FieldState(args.report_date),
    time_tracking_work_type_id: new FieldState(args.time_tracking_work_type_id),
    task_id: new FieldState(args?.task_id),
    vatRate: new FieldState(args?.vatRate),
    vat_amount_in_currency: new FieldState(vatAmountInCurrency),
    accounting_centre_id: new FieldState(args?.accounting_centre_id),
    tag_ids: new FieldState(args?.tag_ids),
    description_id: new FieldState(args?.description_id),
    custom_cost_ratio: new FieldState(args?.custom_cost_ratio),
    accounting_vat_classification_id: new FieldState(
      args?.accounting_vat_classification_id,
    ),
  });
};
